<template>
  <v-container fluid class="ncrm-contracts-home">
    <div class="ncrm-contracts-home__header" data-tour="contracts-header">
      <div class="ncrm-contracts-home__header-left">
        <v-icon class="ncrm-contracts-home__header-icon" size="16">mdi-file-document-outline</v-icon>
        <h1 class="ncrm-contracts-home__title">{{ $t("ncrm.contracts.home.title") }}</h1>
        <span class="ncrm-contracts-home__subtitle">{{ $t("ncrm.contracts.home.subtitle") }}</span>
      </div>
      <div class="ncrm-contracts-home__header-right"><PageHelpButton /></div>
    </div>
    <v-card>
      <div class="ncrm-contracts-home__filter-bar pa-4 pb-3" data-tour="contracts-filters">
        <v-text-field
          v-model="filters.search"
          :label="$t('ncrm.contracts.filters.search')"
          clearable
          hide-details
          class="ncrm-contracts-home__filter-search"
          prepend-inner-icon="mdi-magnify"
          @input="onSearchInput"
          @click:clear="applyFilters"
        />
        <v-select
          v-model="filters.status"
          :label="$t('ncrm.contracts.filters.status')"
          :items="statusItems"
          item-title="label"
          item-value="value"
          hide-details
          class="ncrm-contracts-home__filter-status"
          @update:model-value="applyFilters"
        />
      </div>
      <v-divider />
      <div class="d-flex align-center justify-space-between flex-wrap ga-2 px-4 pt-3 pb-0">
        <NcrmPreferredViewTabs
          v-model="activeView" data-tour="contracts-views"
          :items="viewItems"
          :preferred-value="preferredView"
          :aria-label="$t('ncrm.shared.viewPreferences.tabsAriaLabel')"
          @set-default="setPreferredView"
        />
      </div>
      <NcrmContractsBoardScopeToggle
        v-if="activeView === 'board'"
        :model-value="boardStatusGroup"
        :items="boardStatusGroupItems"
        class="px-4 pt-3"
        @update:model-value="onBoardStatusGroupChange"
      />

      <v-card-text v-if="activeView === 'board'" class="pt-4" data-tour="contracts-list">
        <div v-if="loading" class="ncrm-contracts-home__cards-grid">
          <div
            v-for="n in 6"
            :key="`skel-${n}`"
            class="ncrm-contracts-home__board-card ncrm-contracts-home__board-card--skeleton"
          >
            <div class="d-flex align-start justify-space-between ga-2">
              <v-skeleton-loader type="text" width="55%" />
              <v-skeleton-loader type="chip" width="56px" />
            </div>
            <div class="mt-3" style="display:grid;gap:8px">
              <v-skeleton-loader type="text" width="70%" />
              <v-skeleton-loader type="text" width="60%" />
              <v-skeleton-loader type="text" width="50%" />
            </div>
            <div class="d-flex ga-2 mt-3">
              <v-skeleton-loader type="chip" width="100px" />
              <v-skeleton-loader type="chip" width="100px" />
              <v-skeleton-loader type="chip" width="80px" />
            </div>
          </div>
        </div>

        <v-alert v-else-if="!items.length" type="info">
          {{ $t("ncrm.contracts.home.empty") }}
        </v-alert>

        <v-row v-else dense>
          <v-col cols="12">
            <div class="ncrm-contracts-home__cards-grid">
              <article
                v-for="item in items"
                :key="item.id"
                class="ncrm-contracts-home__board-card"
                :class="{ 'ncrm-contracts-home__board-card--overdue': hasOverduePayments(item) }"
                role="button"
                tabindex="0"
                @click="openContract(item.id)"
                @keydown.enter.prevent="openContract(item.id)"
                @keydown.space.prevent="openContract(item.id)"
              >
                <div class="d-flex align-start justify-space-between ga-2">
                  <div class="ncrm-contracts-home__entry-main">
                    <div class="text-body-1 ncrm-text-strong ncrm-contracts-home__entry-title">
                      {{ displayPartyName(item.company_name, item.person_name) }}
                    </div>
                    <div class="ncrm-contracts-home__entry-id">
                      {{ contractLabel(item) }}
                    </div>
                  </div>
                  <v-chip :color="statusColor(item.status)" class="flex-shrink-0 ncrm-contracts-home__entry-status">
                    {{ statusLabel(item.status) }}
                  </v-chip>
                </div>

                <div class="ncrm-contracts-home__entry-meta mt-2">
                  <div class="ncrm-contracts-home__entry-meta-row">
                    <v-icon size="14" class="ncrm-contracts-home__entry-icon">mdi-account-outline</v-icon>
                    <span>{{ displayValue(item.owner_name) }}</span>
                  </div>
                  <div class="ncrm-contracts-home__entry-meta-row">
                    <v-icon size="14" class="ncrm-contracts-home__entry-icon">mdi-calendar-month-outline</v-icon>
                    <span>{{ formatDate(item.contract_date) }}</span>
                  </div>
                </div>

                <div class="d-flex flex-wrap ga-2 mt-2">
                  <v-chip class="ncrm-contracts-home__chip-amount">
                    {{ formatMoney(item.total_amount, item.currency_code) }}
                  </v-chip>
                  <v-chip :color="onboardingBadgeColor(item)">
                    <v-icon start size="13">{{ onboardingBadgeColor(item) === 'success' ? 'mdi-check-circle-outline' : 'mdi-rocket-launch-outline' }}</v-icon>
                    {{ onboardingBadgeLabel(item) }}
                  </v-chip>
                  <v-chip
                    size="small"
                    :variant="hasOverduePayments(item) ? 'flat' : 'tonal'"
                    :color="hasOverduePayments(item) ? 'error' : 'success'"
                  >
                    {{ $t("ncrm.contracts.home.board.overduePayments", { count: formatNumber(item.overdue_payment_count) }) }}
                  </v-chip>
                </div>

                <div class="ncrm-contracts-home__board-footer mt-auto pt-3">
                  <v-icon size="13" class="ncrm-contracts-home__footer-icon">mdi-clock-outline</v-icon>
                  <span class="text-caption ncrm-contracts-home__footer-date">
                    {{ formatDateTime(item.updated_at) }}
                  </span>
                </div>
              </article>
            </div>
          </v-col>
        </v-row>

        <div
          v-if="pagination.total > 0"
          class="d-flex flex-wrap align-center justify-space-between ga-4 mt-6 ncrm-contracts-home__board-pagination"
        >
          <div class="text-body-2 text-medium-emphasis">
            {{ boardPaginationSummary() }}
          </div>

          <div class="d-flex flex-wrap align-center justify-end ga-4 ncrm-contracts-home__board-pagination-controls">
            <v-select
              :model-value="pagination.limit"
              :items="pageSizeOptions"
              :label="$t('ncrm.contracts.home.board.perPage')"
              density="compact"
              hide-details
              class="ncrm-contracts-home__page-size"
              @update:model-value="onItemsPerPageChange"
            />

            <v-pagination
              v-if="pagination.pages > 1"
              :model-value="pagination.page"
              :length="Math.max(pagination.pages, 1)"
              :total-visible="7"
              density="comfortable"
              @update:model-value="onPageChange"
            />
          </div>
        </div>
      </v-card-text>

      <v-card-text v-else-if="activeView === 'payments'" class="pt-4">
        <div class="ncrm-contracts-home__payment-toolbar">
          <NcrmSegmentedControl
            :model-value="paymentSubView"
            :items="paymentSubViewItems"
            :aria-label="$t('ncrm.contracts.home.payments.toggleAriaLabel')"
            block
            @update:model-value="onPaymentSubViewChange"
          />
        </div>

        <template v-if="paymentSubView === 'due'">
          <div v-if="loading" class="ncrm-contracts-home__cards-grid">
            <div
              v-for="n in 6"
              :key="`payment-skel-${n}`"
              class="ncrm-contracts-home__board-card ncrm-contracts-home__board-card--skeleton"
            >
              <div class="d-flex align-start justify-space-between ga-2">
                <v-skeleton-loader type="text" width="55%" />
                <v-skeleton-loader type="chip" width="56px" />
              </div>
              <div class="mt-3" style="display:grid;gap:8px">
                <v-skeleton-loader type="text" width="70%" />
                <v-skeleton-loader type="text" width="60%" />
                <v-skeleton-loader type="text" width="50%" />
              </div>
              <div class="d-flex ga-2 mt-3">
                <v-skeleton-loader type="chip" width="130px" />
                <v-skeleton-loader type="chip" width="100px" />
              </div>
            </div>
          </div>

          <v-alert v-else-if="!items.length" type="info">
            {{ $t("ncrm.contracts.home.payments.emptyDue") }}
          </v-alert>

          <div v-else class="ncrm-contracts-home__cards-grid">
            <article
              v-for="item in items"
              :key="`payment-due-${item.id}`"
              class="ncrm-contracts-home__board-card"
              :class="{ 'ncrm-contracts-home__board-card--overdue': isPaymentCardOverdue(item) }"
              role="button"
              tabindex="0"
              @click="openContract(item.id)"
              @keydown.enter.prevent="openContract(item.id)"
              @keydown.space.prevent="openContract(item.id)"
            >
              <div class="d-flex align-start justify-space-between ga-2">
                <div class="ncrm-contracts-home__entry-main">
                  <div class="text-body-1 ncrm-text-strong ncrm-contracts-home__entry-title">
                    {{ displayPartyName(item.company_name, item.person_name) }}
                  </div>
                  <div class="ncrm-contracts-home__entry-id">
                    {{ contractLabel(item) }}
                  </div>
                </div>
                <v-chip :color="statusColor(item.status)" class="flex-shrink-0 ncrm-contracts-home__entry-status">
                  {{ statusLabel(item.status) }}
                </v-chip>
              </div>

              <div class="ncrm-contracts-home__entry-meta mt-2">
                <div class="ncrm-contracts-home__entry-meta-row">
                  <v-icon size="14" class="ncrm-contracts-home__entry-icon">mdi-account-outline</v-icon>
                  <span>{{ displayValue(item.owner_name) }}</span>
                </div>
                <div class="ncrm-contracts-home__entry-meta-row">
                  <v-icon size="14" class="ncrm-contracts-home__entry-icon">mdi-calendar-clock-outline</v-icon>
                  <span>{{ formatDate(item.next_payment_due_at) }}</span>
                </div>
              </div>

              <div class="d-flex flex-wrap ga-2 mt-2 ncrm-contracts-home__payment-kpis">
                <v-chip
                  size="small"
                  :variant="isPaymentCardOverdue(item) ? 'flat' : 'tonal'"
                  :color="isPaymentCardOverdue(item) ? 'error' : undefined"
                  :class="{ 'ncrm-contracts-home__chip-amount': !isPaymentCardOverdue(item) }"
                >
                  {{ paymentCardAmountLabel(item) }}
                </v-chip>
                <v-chip
                  size="small"
                  :variant="isPaymentCardOverdue(item) ? 'flat' : 'tonal'"
                  :color="isPaymentCardOverdue(item) ? 'error' : 'success'"
                >
                  <v-icon start size="13">{{ isPaymentCardOverdue(item) ? 'mdi-alert-circle-outline' : 'mdi-calendar-check-outline' }}</v-icon>
                  {{ paymentCardStatusLabel(item) }}
                </v-chip>
              </div>

              <div class="ncrm-contracts-home__board-footer mt-auto pt-3">
                <v-icon size="13" class="ncrm-contracts-home__footer-icon">mdi-clock-outline</v-icon>
                <span class="text-caption ncrm-contracts-home__footer-date">
                  {{ formatDateTime(item.updated_at) }}
                </span>
              </div>
            </article>
          </div>
        </template>

        <template v-else>
          <v-data-table-server
            :headers="paymentHeaders"
            :items="items"
            :loading="loading"
            :items-per-page="pagination.limit"
            :items-length="pagination.total"
            :page="pagination.page"
            item-key="id"
            class="elevation-0 ncrm-contracts-home__table"
            density="comfortable"
            hover
            :row-props="paymentTableRowProps"
            @click:row="handlePaymentRowClick"
            @update:page="onPageChange"
            @update:items-per-page="onItemsPerPageChange"
          >
            <template #item.paid_at="{ item }">
              <div class="d-flex align-center ga-1 ncrm-contracts-home__table-date">
                <v-icon size="13" class="ncrm-contracts-home__table-date-icon">mdi-cash-check</v-icon>
                {{ formatDateTime(item.paid_at || item.updated_at) }}
              </div>
            </template>

            <template #item.amount_paid="{ item }">
              <span class="ncrm-contracts-home__table-amount">
                {{ formatMoney(item.amount_paid, item.currency_code) }}
              </span>
            </template>

            <template #item.contract_number="{ item }">
              <div class="d-flex align-center ga-2">
                <v-icon size="14" class="ncrm-contracts-home__table-cell-icon">mdi-file-document-outline</v-icon>
                <span class="ncrm-text-strong">{{ contractLabel(item) }}</span>
              </div>
            </template>

            <template #item.payment_method="{ item }">
              {{ paymentMethodLabel(item) }}
            </template>

            <template #item.row_nav>
              <v-icon size="18" class="ncrm-contracts-home__table-chevron">mdi-chevron-right</v-icon>
            </template>

            <template #no-data>
              <v-alert type="info" class="ma-4">
                {{ $t("ncrm.contracts.home.payments.emptyPaid") }}
              </v-alert>
            </template>
          </v-data-table-server>
        </template>

        <div
          v-if="pagination.total > 0"
          class="d-flex flex-wrap align-center justify-space-between ga-4 mt-6 ncrm-contracts-home__board-pagination"
        >
          <div class="text-body-2 text-medium-emphasis">
            {{ boardPaginationSummary() }}
          </div>

          <div class="d-flex flex-wrap align-center justify-end ga-4 ncrm-contracts-home__board-pagination-controls">
            <v-select
              :model-value="pagination.limit"
              :items="pageSizeOptions"
              :label="$t('ncrm.contracts.home.board.perPage')"
              density="compact"
              hide-details
              class="ncrm-contracts-home__page-size"
              @update:model-value="onItemsPerPageChange"
            />

            <v-pagination
              v-if="pagination.pages > 1"
              :model-value="pagination.page"
              :length="Math.max(pagination.pages, 1)"
              :total-visible="7"
              density="comfortable"
              @update:model-value="onPageChange"
            />
          </div>
        </div>
      </v-card-text>

      <v-card-text v-else class="pa-0" data-tour="contracts-list">
        <v-data-table-server
          :headers="headers"
          :items="items"
          :loading="loading"
          :items-per-page="pagination.limit"
          :items-length="pagination.total"
          :page="pagination.page"
          v-model:sort-by="sortBy"
          item-key="id"
          class="elevation-0 ncrm-contracts-home__table"
          density="comfortable"
          hover
          :row-props="tableRowProps"
          @click:row="handleRowClick"
          @update:page="onPageChange"
          @update:items-per-page="onItemsPerPageChange"
          @update:sort-by="onSortByChange"
        >
          <template #item.contract_number="{ item }">
            <div class="d-flex align-center ga-2">
              <v-icon size="14" class="ncrm-contracts-home__table-cell-icon">mdi-file-document-outline</v-icon>
              <span class="ncrm-text-strong">{{ contractLabel(item) }}</span>
            </div>
          </template>

          <template #item.status="{ item }">
            <v-chip :color="statusColor(item.status)">
              {{ statusLabel(item.status) }}
            </v-chip>
          </template>

          <template #item.total_amount="{ item }">
            <span class="ncrm-contracts-home__table-amount">
              {{ formatMoney(item.total_amount, item.currency_code) }}
            </span>
          </template>

          <template #item.contract_date="{ item }">
            {{ formatDate(item.contract_date) }}
          </template>

          <template #item.onboarding_count="{ item }">
            <v-chip size="x-small" :color="onboardingBadgeColor(item)">
              <v-icon start size="12">{{ onboardingBadgeColor(item) === 'success' ? 'mdi-check-circle-outline' : 'mdi-rocket-launch-outline' }}</v-icon>
              {{ formatNumber(remainingOnboardingCount(item)) }}
            </v-chip>
          </template>

          <template #item.overdue_payment_count="{ item }">
            <v-chip
              size="x-small"
              :color="hasOverduePayments(item) ? 'error' : 'success'"
            >
              <v-icon start size="12">{{ hasOverduePayments(item) ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}</v-icon>
              {{ formatNumber(item.overdue_payment_count) }}
            </v-chip>
          </template>

          <template #item.updated_at="{ item }">
            <div class="d-flex align-center ga-1 ncrm-contracts-home__table-date">
              <v-icon size="13" class="ncrm-contracts-home__table-date-icon">mdi-clock-outline</v-icon>
              {{ formatDateTime(item.updated_at) }}
            </div>
          </template>

          <!-- Row navigation chevron (replaces "Deschide" button) -->
          <template #item.row_nav>
            <v-icon size="18" class="ncrm-contracts-home__table-chevron">mdi-chevron-right</v-icon>
          </template>

          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.contracts.home.empty") }}
            </v-alert>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3200">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import NcrmPreferredViewTabs from "@/components/ncrm/shared/NcrmPreferredViewTabs.vue";
import NcrmSegmentedControl from "@/components/ncrm/shared/NcrmSegmentedControl.vue";
import NcrmContractsBoardScopeToggle from "@/components/ncrm/contracts/NcrmContractsBoardScopeToggle.vue";
import PageHelpButton from "@/components/tours/PageHelpButton.vue";
import {
  CONTRACT_PAGE_SIZE_OPTIONS,
} from "./contractsHomeShared.mjs";
import { contractsHomeMethods } from "./contractsHomeMethods.mjs";
import {
  buildBoardStatusGroupItems,
  buildContractHomeViewItems,
  buildContractPaymentHeaders,
  buildContractPaymentSubViewItems,
  buildContractStatusItems,
  buildContractTableHeaders,
} from "./contractsHomeOptions.mjs";
import { DEFAULT_BOARD_STATUS_GROUP } from "./contractsHomeFilters.mjs";
export default {
  name: "NcrmContractsHome",
  components: {
    NcrmPreferredViewTabs,
    NcrmSegmentedControl,
    NcrmContractsBoardScopeToggle,
    PageHelpButton,
  },
  data() {
    return {
      loading: false,
      activeView: "board",
      preferredView: "board",
      paymentSubView: "due",
      boardStatusGroup: DEFAULT_BOARD_STATUS_GROUP,
      items: [],
      sortBy: [],
      filters: {
        search: "",
        status: "",
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        pages: 1,
      },
      snackbar: {
        show: false,
        color: "info",
        message: "",
      },
      searchDebounceTimer: null,
    };
  },
  computed: {
    viewItems() {
      return buildContractHomeViewItems((key) => this.$t(key));
    },
    headers() {
      return buildContractTableHeaders((key) => this.$t(key));
    },
    paymentHeaders() {
      return buildContractPaymentHeaders((key) => this.$t(key));
    },
    paymentSubViewItems() {
      return buildContractPaymentSubViewItems((key) => this.$t(key));
    },
    statusItems() {
      return buildContractStatusItems({
        $t: (key) => this.$t(key),
        statusLabel: (status) => this.statusLabel(status),
      });
    },
    boardStatusGroupItems() {
      return buildBoardStatusGroupItems((key) => this.$t(key));
    },
    pageSizeOptions() {
      return CONTRACT_PAGE_SIZE_OPTIONS;
    },
  },
  mounted() {
    this.restorePreferredView();
    this.loadCurrentView();
  },
  watch: {
    activeView(next, previous) {
      if (next === previous) return;
      this.pagination.page = 1;
      this.items = [];
      this.loadCurrentView();
    },
  },
  methods: contractsHomeMethods,
};
</script>

<style scoped src="./NcrmContractsHome.styles.css"></style>
